import { useCallback, useEffect, useState } from 'react';

import { apiFetch } from '@/src/api/apiService';
import { API_ENDPOINTS } from '@/src/api/config';
import type { CatalogPlan } from '@/src/features/onboarding/ownerPlanSelection';

export interface UseProductCatalogResult {
  catalog: CatalogPlan[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Loads the complete server-driven product catalog in one request.
 *
 * The selector deliberately does not pass `feature`: prices for every product
 * must be available before the user commits to a product. The backend remains
 * the source of truth for amounts, currency, billing interval and setup fees.
 */
export const useProductCatalog = (): UseProductCatalogResult => {
  const [catalog, setCatalog] = useState<CatalogPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiFetch<{ prices: CatalogPlan[] }>(
        `${API_ENDPOINTS.SUBSCRIPTIONS}prices/`,
      );
      if (!response.ok || !Array.isArray(response.data?.prices)) {
        throw new Error('catalog_unavailable');
      }
      setCatalog(response.data.prices);
    } catch (err) {
      setCatalog([]);
      setError(err instanceof Error ? err.message : 'catalog_unavailable');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { catalog, isLoading, error, refetch: load };
};

export default useProductCatalog;
